import { prop, path } from 'ramda';
import { CanonicalLoader } from '@wayfinder/vms-state';
import def from './define-action';
import { BANNER_COLORS, BANNER_ICONS } from '../constants';

const INITIAL_STATE = {
    accountId: '',
    isGeneratingSpritesheet: false,
    venueCategory: '',
    venueId: '',
    errors: {
        nameErrors: [],
        contentErrors: [],
    },
};

const BANNER_MESSAGE = {
    FAIL: 'Error generating spritesheet',
    RUNNING: 'Generating spritesheet(s)',
    SUCCESS: 'Successfully generated spritesheet',
    INVALID_PARAMS: 'Venue Category is required',
};

// =====================================================================================================================
// Action Constants
// =====================================================================================================================

const ActionConstants = {
    SET_VENUE_CATEGORY: 'SET_VENUE_CATEGORY',
    SET_VENUE: 'SET_VENUE',
    SET_ACCOUNT: 'SET_ACCOUNT',
    CLEAR_SPECIFICITY: 'CLEAR_SPECIFICITY',
    SET_ERRORS: 'SET_ERRORS',
    BEGIN_GENERATE_SPRITESHEET: 'BEGIN_GENERATE_SPRITESHEET',
    END_GENERATE_SPRITESHEET: 'END_GENERATE_SPRITESHEET',
    MESSAGE_BANNER: 'MESSAGE_BANNER',
    CLEAR_BANNER: 'CLEAR_BANNER',
};

// =====================================================================================================================
// Actions
// =====================================================================================================================

const Actions = {
    setErrors: def(ActionConstants.SET_ERRORS, 'errors'),
    setVenueCategory: def(ActionConstants.SET_VENUE_CATEGORY, 'venueCategory'),
    setVenue: def(ActionConstants.SET_VENUE, 'venueId'),
    setAccount: def(ActionConstants.SET_ACCOUNT, 'accountId'),
    clearSpecificity: def(ActionConstants.CLEAR_SPECIFICITY),
    beginGenerate: def(ActionConstants.BEGIN_GENERATE_SPRITESHEET),
    endGenerate: def(ActionConstants.END_GENERATE_SPRITESHEET),
    messageBanner: def(ActionConstants.MESSAGE_BANNER, 'message', 'color', 'icon'),
    clearBanner: def(ActionConstants.CLEAR_BANNER),
};

// =====================================================================================================================
// Thunks
// =====================================================================================================================

/*
 * Generate a spritesheet for the given venueCategory, venueId, and accountId
 * @sig generateSpritesheet :: () -> (DispatchFunction, Object) -> Promise ()
 */
const generateSpritesheet = () => async (dispatch, getState) => {
    const state = getState();
    const { venueCategory, venueId, accountId } = getSpecificity(state);

    if (!venueCategory) {
        dispatch(Actions.messageBanner(BANNER_MESSAGE.INVALID_PARAMS, BANNER_COLORS.RED, BANNER_ICONS.FAIL));
        return;
    }

    try {
        dispatch(Actions.beginGenerate());
        dispatch(Actions.messageBanner(BANNER_MESSAGE.RUNNING, BANNER_COLORS.BLUE, BANNER_ICONS.RUNNING));
        const errors = await CanonicalLoader.generateSpritesheet(venueCategory, venueId, accountId);

        if (errors) dispatch(Actions.setErrors(errors));

        dispatch(Actions.endGenerate());
        dispatch(Actions.messageBanner(BANNER_MESSAGE.SUCCESS, BANNER_COLORS.GREEN, BANNER_ICONS.SUCCESS));
    } catch (err) {
        dispatch(Actions.endGenerate());
        dispatch(Actions.messageBanner(BANNER_MESSAGE.FAIL, BANNER_COLORS.RED, BANNER_ICONS.FAIL));
        console.error(err);
    }
};

const Thunks = { generateSpritesheet };

// =====================================================================================================================
// Reducer
// =====================================================================================================================

const reducer = (state = INITIAL_STATE, action) => {
    switch (action.type) {
        case ActionConstants.MESSAGE_BANNER:
            return handleBanner(state, action);
        case ActionConstants.CLEAR_BANNER:
            return { ...state, banner: { message: '', color: '', icon: '' } };
        case ActionConstants.SET_ERRORS:
            return handleSetErrors(state, action);
        case ActionConstants.SET_VENUE_CATEGORY:
            return handleSetVenueCategory(state, action);
        case ActionConstants.SET_VENUE:
            return handleSetVenue(state, action);
        case ActionConstants.SET_ACCOUNT:
            return handleSetAccount(state, action);
        case ActionConstants.CLEAR_SPECIFICITY:
            return handleClearSpecificity(state);
        case ActionConstants.BEGIN_GENERATE_SPRITESHEET:
            return { ...state, isGeneratingSpritesheet: true };
        case ActionConstants.END_GENERATE_SPRITESHEET:
            return { ...state, isGeneratingSpritesheet: false };
        default:
            return state;
    }
};

const handleBanner = (state, action) => ({
    ...state,
    banner: {
        message: action.message || 'Unknown Error',
        color: action.color,
        icon: action.icon,
        isVisible: true,
    },
});

const handleSetErrors = (state, action) => ({ ...state, errors: action.errors });
const handleSetVenueCategory = (state, action) => ({ ...state, venueCategory: action.venueCategory });
const handleSetVenue = (state, action) => ({ ...state, venueId: action.venueId });
const handleSetAccount = (state, action) => ({ ...state, accountId: action.accountId });
const handleClearSpecificity = state => ({ ...state, venueCategory: '', venueId: '', accountId: '' });

// =====================================================================================================================
// Selectors
// =====================================================================================================================

const getSpecificity = masterState => prop('spritesheet', masterState);
const getErrors = masterState => path(['spritesheet', 'errors'], masterState);
const getIsGeneratingSpritesheet = masterState => path(['spritesheet', 'isGeneratingSpritesheet'], masterState);
const getSpritesheetMessageBanner = masterState => path(['spritesheet', 'banner'], masterState);

const Selectors = {
    getSpecificity,
    getErrors,
    getIsGeneratingSpritesheet,
    getSpritesheetMessageBanner,
};

export default reducer;

export { reducer, Actions, Selectors, Thunks };
